import { PackageSearch, Trash2 } from 'lucide-react'

import { PromotionOptionSelect } from './PromotionOptionSelect'

import { Badge } from '@/app/components/ui/badge'
import { Button } from '@/app/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select'
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/app/components/ui/sheet'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/app/components/ui/tabs'
import type { PromotionOption } from '@/app/services/promotion.service'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  products: PromotionOption[]
  excludedProducts: PromotionOption[]
  categories: PromotionOption[]
  departments: PromotionOption[]
  onProductsChange: (items: PromotionOption[]) => void
  onExcludedProductsChange: (items: PromotionOption[]) => void
  onCategoriesChange: (items: PromotionOption[]) => void
  onDepartmentsChange: (items: PromotionOption[]) => void
}

function CatalogRules({ items, onChange }: { items: PromotionOption[]; onChange: (items: PromotionOption[]) => void }) {
  return (
    <div className="max-h-[55vh] space-y-2 overflow-y-auto pr-1">
      {items.map((item) => (
        <div key={item.id} className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card p-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-foreground">{item.name}</p>
            {item.is_active === false && <p className="text-xs text-muted-foreground">Catálogo inactivo</p>}
          </div>
          <Button type="button" variant="ghost" size="icon" aria-label={`Quitar ${item.name}`} onClick={() => onChange(items.filter((current) => current.id !== item.id))}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ))}
      {items.length === 0 && <p className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">Aún no hay reglas seleccionadas.</p>}
    </div>
  )
}

export function PromotionApplicabilitySheet(props: Props) {
  const productRules = [
    ...props.products.map((item) => ({ ...item, effect: 'include' as const })),
    ...props.excludedProducts.map((item) => ({ ...item, effect: 'exclude' as const })),
  ]

  const setProductEffect = (item: PromotionOption, effect: 'include' | 'exclude' | 'none') => {
    props.onProductsChange(props.products.filter((current) => current.id !== item.id))
    props.onExcludedProductsChange(props.excludedProducts.filter((current) => current.id !== item.id))
    if (effect === 'include') props.onProductsChange([...props.products.filter((current) => current.id !== item.id), item])
    if (effect === 'exclude') props.onExcludedProductsChange([...props.excludedProducts.filter((current) => current.id !== item.id), item])
  }

  const addIncluded = (items: PromotionOption[]) => {
    const ids = new Set(items.map((item) => item.id))
    props.onProductsChange(items)
    props.onExcludedProductsChange(props.excludedProducts.filter((item) => !ids.has(item.id)))
  }

  const addExcluded = (items: PromotionOption[]) => {
    const ids = new Set(items.map((item) => item.id))
    props.onExcludedProductsChange(items)
    props.onProductsChange(props.products.filter((item) => !ids.has(item.id)))
  }

  return (
    <Sheet open={props.open} onOpenChange={props.onOpenChange}>
      <SheetContent className="w-full overflow-hidden bg-popover sm:max-w-2xl">
        <SheetHeader className="border-b border-border pr-10">
          <SheetTitle className="flex items-center gap-2"><PackageSearch className="h-5 w-5" />Administrar aplicabilidad</SheetTitle>
          <SheetDescription>Las categorías y departamentos se guardan como reglas sin desplegar todos sus productos; una exclusión directa siempre gana.</SheetDescription>
        </SheetHeader>

        <Tabs defaultValue="categories" className="min-h-0 flex-1 px-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="categories">Categorías ({props.categories.length})</TabsTrigger>
            <TabsTrigger value="departments">Departamentos ({props.departments.length})</TabsTrigger>
            <TabsTrigger value="products">Productos ({productRules.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="categories" className="space-y-3 overflow-hidden">
            <PromotionOptionSelect kind="categories" selected={props.categories} onChange={props.onCategoriesChange} placeholder="Buscar y agregar categorías..." showSelectedList={false} allowSelectedFilter />
            <CatalogRules items={props.categories} onChange={props.onCategoriesChange} />
          </TabsContent>

          <TabsContent value="departments" className="space-y-3 overflow-hidden">
            <PromotionOptionSelect kind="departments" selected={props.departments} onChange={props.onDepartmentsChange} placeholder="Buscar y agregar departamentos..." showSelectedList={false} allowSelectedFilter />
            <CatalogRules items={props.departments} onChange={props.onDepartmentsChange} />
          </TabsContent>

          <TabsContent value="products" className="space-y-3 overflow-hidden">
            <div className="grid gap-2 sm:grid-cols-2">
              <PromotionOptionSelect kind="products" selected={props.products} onChange={addIncluded} placeholder="Agregar productos incluidos..." showSelectedList={false} allowSelectedFilter />
              <PromotionOptionSelect kind="products" selected={props.excludedProducts} onChange={addExcluded} placeholder="Agregar exclusiones..." showSelectedList={false} allowSelectedFilter />
            </div>
            <div className="max-h-[52vh] space-y-2 overflow-y-auto pr-1">
              {productRules.map((item) => (
                <div key={item.id} className="flex items-center gap-3 rounded-lg border border-border bg-card p-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">{item.name}</p>
                    {(item.sku || item.barcode) && <p className="truncate text-xs text-muted-foreground">{item.sku || item.barcode}</p>}
                  </div>
                  <Select value={item.effect} onValueChange={(value) => setProductEffect(item, value as 'include' | 'exclude' | 'none')}>
                    <SelectTrigger className="w-28" size="sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="include">Incluir</SelectItem>
                      <SelectItem value="exclude">Excluir</SelectItem>
                      <SelectItem value="none">Sin regla</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}
              {productRules.length === 0 && <p className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">No hay productos específicos. Puedes aplicar la promoción sólo mediante categorías o departamentos.</p>}
            </div>
          </TabsContent>
        </Tabs>

        <SheetFooter className="border-t border-border">
          <div className="flex flex-wrap gap-2 text-xs">
            <Badge variant="secondary">{props.categories.length} categorías</Badge>
            <Badge variant="secondary">{props.departments.length} departamentos</Badge>
            <Badge variant="secondary">{props.products.length} inclusiones</Badge>
            <Badge variant="secondary">{props.excludedProducts.length} exclusiones</Badge>
          </div>
          <Button type="button" onClick={() => props.onOpenChange(false)}>Listo</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
